import { createBrowserRouter } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AppLayout } from './components/layout/app-layout'
import { MaintenanceLayout } from './components/layout/maintenance-layout'

// Loading component for lazy-loaded routes
const PageLoader = () => (
    <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
)

// Lazy load wrapper
const lazyLoad = (importFn: () => Promise<{ default: React.ComponentType }>) => {
    const Component = lazy(importFn)
    return (
        <Suspense fallback={<PageLoader />}>
            <Component />
        </Suspense>
    )
}

// ==================== AUTH ROUTES ====================
const LoginPage = () => lazyLoad(() => import('./app/(auth)/login/page'))
const RegisterPage = () => lazyLoad(() => import('./app/(auth)/register/page'))
const ConfirmationCodePage = () => lazyLoad(() => import('./app/(auth)/confirmation-code/page'))

// ==================== MAIN ROUTES ====================
const DashboardPage = () => lazyLoad(() => import('./app/page'))
const OnboardingPage = () => lazyLoad(() => import('./app/onboarding/page'))
const MaintenanceOnboardingPage = () => lazyLoad(() => import('./app/maintenance-onboarding/page'))
const ProfilePage = () => lazyLoad(() => import('./app/profile/page'))

// ==================== LOADS ====================
const LoadsPage = () => lazyLoad(() => import('./app/loads/page'))
const LoadDetailPage = () => lazyLoad(() => import('./app/loads/detail-v2/page'))
// const LoadDetailLegacyPage = () => lazyLoad(() => import('./app/loads/detail-new/page'))
const LoadNewPage = () => lazyLoad(() => import('./app/loads/new/page'))
const RecurringLoadsPage = () => lazyLoad(() => import('./app/loads/recurring/page'))
const RecurringLoadNewPage = () => lazyLoad(() => import('./app/loads/recurring/new/page'))
const RecurringLoadEditPage = () => lazyLoad(() => import('./app/loads/recurring/edit/page'))
const LoadTemplatesPage = () => lazyLoad(() => import('./app/loads/templates/page'))
const LoadTemplateNewPage = () => lazyLoad(() => import('./app/loads/templates/new/page'))
const LoadTemplateEditPage = () => lazyLoad(() => import('./app/loads/templates/edit/page'))
const LoadCalculatorPage = () => lazyLoad(() => import('./app/loads/calculator/page'))

// ==================== CARRIERS ====================
const CarriersPage = () => lazyLoad(() => import('./app/carriers/page'))
const CarrierDetailPage = () => lazyLoad(() => import('./app/carriers/detail/page'))
const CarrierNewPage = () => lazyLoad(() => import('./app/carriers/new/page'))
const CarrierEditPage = () => lazyLoad(() => import('./app/carriers/edit/page'))

// ==================== CUSTOMERS ====================
const CustomersPage = () => lazyLoad(() => import('./app/customers/page'))
const CustomerDetailPage = () => lazyLoad(() => import('./app/customers/detail/page'))

// ==================== QUOTES ====================
const QuotesPage = () => lazyLoad(() => import('./app/quotes/page'))
const QuoteDetailPage = () => lazyLoad(() => import('./app/quotes/detail/page'))
const QuoteNewPage = () => lazyLoad(() => import('./app/quotes/new/page'))
const QuoteEditPage = () => lazyLoad(() => import('./app/quotes/edit/page'))

// ==================== DRIVERS ====================
const DriversPage = () => lazyLoad(() => import('./app/drivers/page'))
const DriverDetailPage = () => lazyLoad(() => import('./app/drivers/detail/page'))

// ==================== FLEET ====================
const TrucksPage = () => lazyLoad(() => import('./app/fleet/truck/page'))
const TrailersPage = () => lazyLoad(() => import('./app/fleet/trailer/page'))
const TruckDetailPage = () => lazyLoad(() => import('./app/fleet/truck/detail/page'))
const TrailerDetailPage = () => lazyLoad(() => import('./app/fleet/trailer/detail/page'))

// ==================== INVOICES ====================
const InvoicesPage = () => lazyLoad(() => import('./app/invoices/page'))
const InvoiceDetailPage = () => lazyLoad(() => import('./app/invoices/detail/page'))

// ==================== FACTORING ====================
const FactoringPage = () => lazyLoad(() => import('./app/factoring/page'))
const FactoringBatchDetailPage = () => lazyLoad(() => import('./app/factoring/detail/page'))

// ==================== EXPENSES ====================
const ExpensesPage = () => lazyLoad(() => import('./app/expenses/page'))
const ExpenseNewPage = () => lazyLoad(() => import('./app/expenses/new/page'))
const ExpenseDetailPage = () => lazyLoad(() => import('./app/expenses/detail/page'))
const ExpenseEditPage = () => lazyLoad(() => import('./app/expenses/edit/page'))

// ==================== FUEL ====================
const FuelPage = () => lazyLoad(() => import('./app/fuel/page'))
const FuelNewPage = () => lazyLoad(() => import('./app/fuel/new/page'))
const FuelDetailPage = () => lazyLoad(() => import('./app/fuel/detail/page'))

// ==================== SETTLEMENTS ====================
const SettlementsPage = () => lazyLoad(() => import('./app/settlements/page'))
const SettlementDetailPage = () => lazyLoad(() => import('./app/settlements/detail/page'))

// ==================== PAYSTUBS ====================
const PaystubsPage = () => lazyLoad(() => import('./app/paystubs/page'))
const PaystubDetailPage = () => lazyLoad(() => import('./app/paystubs/detail/page'))

// ==================== MAINTENANCE ====================
const MaintenancePage = () => lazyLoad(() => import('./app/maintenance/page'))
const MaintenanceDetailPage = () => lazyLoad(() => import('./app/maintenance/detail/page'))
const WorkOrdersPage = () => lazyLoad(() => import('./app/maintenance/work-orders/page'))
const WorkOrderDetailPage = () => lazyLoad(() => import('./app/maintenance/work-orders/detail/page'))
const WorkOrderEditPage = () => lazyLoad(() => import('./app/maintenance/work-orders/edit/page'))
const PartsPage = () => lazyLoad(() => import('./app/maintenance/parts/page'))
const PartDetailPage = () => lazyLoad(() => import('./app/maintenance/parts/detail/page'))
const FleetMaintenancePage = () => lazyLoad(() => import('./app/maintenance/fleet/page'))
const FleetMaintenanceDetailPage = () => lazyLoad(() => import('./app/maintenance/fleetDetail/page'))
const TechniciansPage = () => lazyLoad(() => import('./app/maintenance/technicians/page'))
const TechnicianDetailPage = () => lazyLoad(() => import('./app/maintenance/technicians/detail/page'))
const MaintenanceInspectionsPage = () => lazyLoad(() => import('./app/maintenance/inspections/page'))
const MaintenanceInspectionDetailPage = () => lazyLoad(() => import('./app/inspections/detail/page'))
const YardsPage = () => lazyLoad(() => import('./app/maintenance/yards/page'))
const YardDetailPage = () => lazyLoad(() => import('./app/maintenance/yards/detail/page'))
const YardEventDetailPage = () => lazyLoad(() => import('./app/maintenance/yards/event-detail/page'))
const YardCreateEventPage = () => lazyLoad(() => import('./app/maintenance/yards/create-event/page'))
const YardNewPage = () => lazyLoad(() => import('./app/maintenance/yards/new/page'))
const YardFormTemplatesPage = () => lazyLoad(() => import('./app/maintenance/yards/form-templates/page'))
const YardFleetOverviewPage = () => lazyLoad(() => import('./app/maintenance/yards/fleet-overview/page'))

// ==================== INSPECTIONS ====================
const InspectionsPage = () => lazyLoad(() => import('./app/inspections/page'))
const InspectionNewPage = () => lazyLoad(() => import('./app/inspections/new/page'))
const InspectionDetailPage = () => lazyLoad(() => import('./app/inspections/detail/page'))

// ==================== COMPLIANCE ====================
const CompliancePage = () => lazyLoad(() => import('./app/compliance/page'))
const ComplianceDetailPage = () => lazyLoad(() => import('./app/compliance/detail/page'))

// ==================== INTEGRATIONS ====================
const IntegrationsPage = () => lazyLoad(() => import('./app/integrations/page'))
const QuickbooksPage = () => lazyLoad(() => import('./app/integrations/quickbooks/page'))
const RelayPage = () => lazyLoad(() => import('./app/integrations/relay/page'))
const RelayConfigPage = () => lazyLoad(() => import('./app/integrations/relay/configure/page'))
const FleetrockPage = () => lazyLoad(() => import('./app/integrations/fleetrock/page'))

// ==================== TRACKING ====================
const TrackingPage = () => lazyLoad(() => import('./app/tracking/page'))
const TrackingViewPage = () => lazyLoad(() => import('./app/tracking/view/page'))

// ==================== SETTINGS ====================
const SettingsPage = () => lazyLoad(() => import('./app/settings/page'))
const SubscriptionPlansPage = () => lazyLoad(() => import('./app/settings/subscription/plans/page'))
const MyInvitationsPage = () => lazyLoad(() => import('./app/my-invitations/page'))

// ==================== COMING SOON ====================
const ComingSoonPage = () => lazyLoad(() => import('./app/coming-soon/page'))

// ==================== NOT FOUND ====================
const NotFoundPage = () => lazyLoad(() => import('./app/not-found/page'))

// ==================== OTHER ROUTES ====================
const AnalyticsPage = () => lazyLoad(() => import('./app/analytics/page'))
const AuditLogPage = () => lazyLoad(() => import('./app/audit-log/page'))
const DispatchPage = () => lazyLoad(() => import('./app/dispatch/page'))
const DocumentsPage = () => lazyLoad(() => import('./app/documents/page'))
const EdiPage = () => lazyLoad(() => import('./app/edi/page'))
const HelpPage = () => lazyLoad(() => import('./app/help/page'))
const HosPage = () => lazyLoad(() => import('./app/hos/page'))
const IftaPage = () => lazyLoad(() => import('./app/ifta/page'))
const PendingInvitationsPage = () => lazyLoad(() => import('./app/pending-invitations/page'))
const LanesPage = () => lazyLoad(() => import('./app/lanes/page'))
const MarginsPage = () => lazyLoad(() => import('./app/margins/page'))
const MarketplacePage = () => lazyLoad(() => import('./app/marketplace/page'))
const MessagingPage = () => lazyLoad(() => import('./app/messaging/page'))
const PortalPage = () => lazyLoad(() => import('./app/portal/page'))
const PortalDemoPage = () => lazyLoad(() => import('./app/portal-demo/page'))
const RatesPage = () => lazyLoad(() => import('./app/rates/page'))
const ReportsPage = () => lazyLoad(() => import('./app/reports/page'))
const AgingReportPage = () => lazyLoad(() => import('./app/reports/aging/page'))

// ==================== STRIPE CALLBACKS ====================
const StripeSuccessPage = () => lazyLoad(() => import('./app/stripe/success/page'))
const StripeFailurePage = () => lazyLoad(() => import('./app/stripe/failure/page'))

// ==================== SAMSARA CALLBACK ====================
const SamsaraCallbackPage = () => lazyLoad(() => import('./app/samsara/page'))

// ==================== QUICKBOOKS CALLBACK ====================
const QuickBooksCallbackPage = () => lazyLoad(() => import('./app/integrations/quickbooks/callback/page'))

// ==================== ROUTER CONFIGURATION ====================
export const router = createBrowserRouter([
    // Auth routes (no layout)
    {
        path: '/login',
        element: <LoginPage />,
    },
    {
        path: '/register',
        element: <RegisterPage />,
    },
    {
        path: '/confirmation-code',
        element: <ConfirmationCodePage />,
    },
    {
        path: '/onboarding',
        element: <OnboardingPage />,
    },
    {
        path: '/maintenance-onboarding',
        element: <MaintenanceOnboardingPage />,
    },
    // {
    //     path: '/invitations/:token',
    //     element: <InvitationAcceptPage />,
    // },


    // Stripe callbacks (no layout)
    {
        path: '/stripe/success',
        element: <StripeSuccessPage />,
    },
    {
        path: '/stripe/failure',
        element: <StripeFailurePage />,
    },
    {
        path: '/stripe/cancel',
        element: <StripeFailurePage />,
    },

    // Samsara OAuth callback (no layout)
    {
        path: '/samsara',
        element: <SamsaraCallbackPage />,
    },

    // QuickBooks OAuth callback (no layout)
    {
        path: '/quickbooks',
        element: <QuickBooksCallbackPage />,
    },

    // Main app routes (with layout)
    {
        path: '/',
        element: <AppLayout />,
        children: [
            // Dashboard
            { index: true, element: <DashboardPage /> },
            { path: 'profile', element: <ProfilePage /> },
            {
                path: '/invitations',
                element: <PendingInvitationsPage />,
            },
            {
                path: '/invitations/:token',
                element: <PendingInvitationsPage />,
            },
            // Loads
            { path: 'loads', element: <LoadsPage /> },
            { path: 'loads/:id', element: <LoadDetailPage /> },
            { path: 'loads/new', element: <LoadNewPage /> },
            { path: 'loads/recurring', element: <RecurringLoadsPage /> },
            { path: 'loads/recurring/new', element: <RecurringLoadNewPage /> },
            { path: 'loads/recurring/:id/edit', element: <RecurringLoadEditPage /> },
            { path: 'loads/templates', element: <LoadTemplatesPage /> },
            { path: 'loads/templates/new', element: <LoadTemplateNewPage /> },
            { path: 'loads/templates/:id/edit', element: <LoadTemplateEditPage /> },
            { path: 'loads/calculator', element: <LoadCalculatorPage /> },

            // Carriers
            { path: 'carriers', element: <CarriersPage /> },
            { path: 'carriers/:id', element: <CarrierDetailPage /> },
            { path: 'carriers/new', element: <CarrierNewPage /> },
            { path: 'carriers/:id/edit', element: <CarrierEditPage /> },

            // Customers
            { path: 'customers', element: <CustomersPage /> },
            { path: 'customers/:id', element: <CustomerDetailPage /> },

            // Quotes
            { path: 'quotes', element: <QuotesPage /> },
            { path: 'quotes/:id', element: <QuoteDetailPage /> },
            { path: 'quotes/new', element: <QuoteNewPage /> },
            { path: 'quotes/:id/edit', element: <QuoteEditPage /> },

            // Drivers
            { path: 'drivers', element: <DriversPage /> },
            { path: 'drivers/:id', element: <DriverDetailPage /> },

            // Fleet
            { path: 'trucks', element: <TrucksPage /> },
            { path: 'trailers', element: <TrailersPage /> },
            { path: 'fleet/trucks/:id', element: <TruckDetailPage /> },
            { path: 'fleet/trailers/:id', element: <TrailerDetailPage /> },

            // Invoices
            { path: 'invoices', element: <InvoicesPage /> },
            { path: 'invoices/:id', element: <InvoiceDetailPage /> },

            // Factoring
            { path: 'factoring', element: <FactoringPage /> },
            { path: 'factoring/:id', element: <FactoringBatchDetailPage /> },

            // Settlements
            { path: 'settlements', element: <SettlementsPage /> },
            { path: 'settlements/:id', element: <SettlementDetailPage /> },

            // Paystubs
            { path: 'paystubs', element: <PaystubsPage /> },
            { path: 'paystubs/:id', element: <PaystubDetailPage /> },

            // Payments

            // Expenses
            { path: 'expenses', element: <ExpensesPage /> },
            { path: 'expenses/new', element: <ExpenseNewPage /> },
            { path: 'expenses/:id', element: <ExpenseDetailPage /> },
            { path: 'expenses/:id/edit', element: <ExpenseEditPage /> },

            // Fuel
            { path: 'fuel', element: <FuelPage /> },
            { path: 'fuel/new', element: <FuelNewPage /> },
            { path: 'fuel/:id', element: <FuelDetailPage /> },

            // Maintenance (moved to MaintenanceLayout)

            // Inspections
            { path: 'inspections', element: <InspectionsPage /> },
            { path: 'inspections/new', element: <InspectionNewPage /> },
            { path: 'inspections/:id', element: <InspectionDetailPage /> },

            // Compliance
            { path: 'compliance', element: <CompliancePage /> },
            { path: 'compliance/:id', element: <ComplianceDetailPage /> },

            // Integrations
            { path: 'integrations', element: <IntegrationsPage /> },
            { path: 'integrations/quickbooks', element: <QuickbooksPage /> },
            { path: 'integrations/relay', element: <RelayPage /> },
            { path: 'integrations/relay/configure', element: <RelayConfigPage /> },
            { path: 'integrations/fleetrock', element: <FleetrockPage /> },

            // Tracking
            { path: 'tracking', element: <TrackingPage /> },
            { path: 'tracking/:id', element: <TrackingViewPage /> },

            // Settings
            { path: 'settings', element: <SettingsPage /> },
            { path: 'settings/subscription/plans', element: <SubscriptionPlansPage /> },
            { path: 'my-invitations', element: <MyInvitationsPage /> },

            // Other routes
            { path: 'analytics', element: <AnalyticsPage /> },
            { path: 'audit-log', element: <AuditLogPage /> },
            { path: 'dispatch', element: <DispatchPage /> },
            { path: 'documents', element: <DocumentsPage /> },
            { path: 'edi', element: <EdiPage /> },
            { path: 'help', element: <HelpPage /> },
            { path: 'hos', element: <HosPage /> },
            { path: 'ifta', element: <IftaPage /> },
            { path: 'lanes', element: <LanesPage /> },
            { path: 'margins', element: <MarginsPage /> },
            { path: 'marketplace', element: <MarketplacePage /> },
            { path: 'messaging', element: <MessagingPage /> },
            { path: 'portal', element: <PortalPage /> },
            { path: 'portal-demo', element: <PortalDemoPage /> },
            { path: 'rates', element: <RatesPage /> },
            { path: 'reports', element: <ReportsPage /> },
            { path: 'reports/aging', element: <AgingReportPage /> },

            // Coming Soon (placeholder for unfinished features)
            { path: 'coming-soon', element: <ComingSoonPage /> },

            { path: 'yards', element: <YardsPage /> },
            { path: 'yards/new', element: <YardNewPage /> },
            { path: 'yards/fleet-overview', element: <YardFleetOverviewPage /> },
            { path: 'yards/form-templates', element: <YardFormTemplatesPage /> },
            { path: 'yards/events/new', element: <YardCreateEventPage /> },
            { path: 'yards/:id', element: <YardDetailPage /> },
            { path: 'yards/:id/events/new', element: <YardCreateEventPage /> },
            { path: 'yards/:id/events/:eventId', element: <YardEventDetailPage /> },

            // Catch-all 404 within layout
            { path: '*', element: <NotFoundPage /> },
        ],
    },

    // Maintenance routes (with MaintenanceLayout)
    {
        path: '/maintenance',
        element: <MaintenanceLayout />,
        children: [
            { index: true, element: <MaintenancePage /> },
            { path: 'fleet', element: <FleetMaintenancePage /> },
            { path: 'fleet/:id', element: <FleetMaintenanceDetailPage /> },
            { path: ':id', element: <MaintenanceDetailPage /> },
            { path: 'work-orders', element: <WorkOrdersPage /> },
            { path: 'work-orders/:id', element: <WorkOrderDetailPage /> },
            { path: 'work-orders/:id/edit', element: <WorkOrderEditPage /> },
            { path: 'parts', element: <PartsPage /> },
            { path: 'parts/:id', element: <PartDetailPage /> },
            { path: 'technicians', element: <TechniciansPage /> },
            { path: 'technicians/:id', element: <TechnicianDetailPage /> },
            { path: 'inspections', element: <MaintenanceInspectionsPage /> },
            { path: 'inspections/:id', element: <MaintenanceInspectionDetailPage /> },

        ],
    },

    // Catch-all 404 for routes outside layout
    {
        path: '*',
        element: <NotFoundPage />,
    },
])
